// @fileoverview : 재고관리 > 재고현황

import { customFunctions } from "@/config/customFunction";
import { itemTypeColor } from "@/constants/items";
import { DateHeader } from "@/features/inventory/components/DateHeader";
import { LedgerDetailForm } from "@/features/inventory/components/Form/detail/DetailForm";
import { ActionHeader } from "@/features/inventory/components/InventoryActionHeader";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { LocationDetailForm } from "@/features/location/detail/LocationDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import { useLocationsCode } from "@/features/ui/autoComplete/location/auto-useGetLocationsQuery";
import timeUtil from "@/utils/timeUtil";
import { setToLocaleString } from "@/utils/unitMark";
import { Avatar, Badge, Checkbox, Flex, Text, Tooltip } from "@mantine/core";
import {
  InventoriesBetweenGet200Response,
  InventoriesBetweenGet200ResponseRowsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconFilterPlus } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { Column, SelectColumn } from "react-data-grid";
import { useTranslation } from "react-i18next";

// 서버로 부터 수신한 로케이션 정보
type LocationChipInfo = {
  [key: string]: string;
};

export const OutsourceIncoming = () => {
  const { openModal } = useModal();
  const { t } = useTranslation();
  const { sort, setSort, endDate, setParams, startDate, query, setQuery, setSelectedRows } =
    useStandardLayout();

  const chipInitRef = useRef(false); // 최초 실행 여부 플래그
  const prevQueryRef = useRef<any[]>([]); // query값 저장
  const [locationChipInfo, setLocationChipInfo] = useState<LocationChipInfo>(); // 서버로 부터 수신한 로케이션 정보
  const [chipState, setChipState] = useState<string[]>([]);

  // 서버로 부터 로케이션 정보 수신
  const { data: locations } = useLocationsCode(customFunctions.ADD_INVENTORY_LOCATION_FILTER, null);

  // const [isBeta] = useLocalStorage<boolean>({
  //   key: "isBeta",
  //   defaultValue: false, // 기본값은 모든 컬럼이 체크됨
  // });
  const columns: Column<InventoriesBetweenGet200ResponseRowsInner>[] = useMemo(() => {
    return [
      {
        ...SelectColumn,
        resizable: true,
      },
      {
        key: "lotId",
        name: t("lot.id"),
        sortable: true,
        resizable: true,
      },
      {
        key: "itemCode",
        name: t("item.code"),
        sortable: true,
        resizable: true,
        formatter: ({ row }: any) => {
          const detailItemAction = () => {
            openModal(
              <ItemsDetailForm itemCode={row.itemCode} />,
              null,
              `${t("common.detail", { item: `${t("item.item")}` })}`,
            );
          };
          return <LinkAnchor onClick={detailItemAction}>{row.itemCode}</LinkAnchor>;
        },
      },
      {
        key: "itemName",
        name: t("item.name"),
        sortable: true,
        resizable: true,
      },
      {
        key: "spec",
        name: t("item.spec"),
        sortable: true,
        resizable: true,
      },
      {
        key: "texture",
        name: t("item.texture"),
        sortable: true,
        resizable: true,
      },
      // {
      //   key: "thickness",
      //   name: t("item.thickness"),
      //   sortable: true,
      //   resizable: true,
      // },
      {
        key: "width",
        name: t("item.width"),
        sortable: true,
        resizable: true,
      },
      // {
      //   key: "pitch",
      //   name: t("item.pitch"),
      //   sortable: true,
      //   resizable: true,
      // },
      // {
      //   key: "specificGravity",
      //   name: t("item.specificGravity"),
      //   sortable: true,
      //   resizable: true,
      // },
      // {
      //   key: "cvt",
      //   name: t("item.cvt"),
      //   sortable: true,
      //   resizable: true,
      // },
      // {
      //   key: "grossWeight",
      //   name: t("item.grossWeight"),
      //   sortable: true,
      //   resizable: true,
      // },
      {
        key: "itemType",
        name: t("item.type"),
        sortable: true,
        resizable: true,
        formatter: ({ row }: any) => {
          return (
            <Badge size="lg" color={itemTypeColor[row?.itemType as keyof typeof itemTypeColor]}>
              {t(row?.itemType as string)}
            </Badge>
          );
        },
      },
      {
        key: "lot.name",
        name: t("lot.name"),
        sortable: true,
        resizable: true,
        formatter: ({ row }) => <Flex align="center">{row.lot?.name}</Flex>,
      },
      {
        key: "lot.expiration",
        name: t("lot.lotExpiration"),
        sortable: true,
        resizable: true,
        formatter: ({ row }) => (
          <Flex align="center">
            {row.lot?.expiration === null ? "" : timeUtil(row.lot?.expiration!)}
          </Flex>
        ),
      },
      {
        key: "locationName",
        name: t("location.name"),
        sortable: true,
        resizable: true,
        formatter: ({ row }) => {
          const detailItemAction = () => {
            openModal(
              <LocationDetailForm locationCode={row.locationCode ?? ""} />,
              null,
              `${t("common.detail", { item: `${t("location.location")}` })}`,
            );
          };
          return <LinkAnchor onClick={detailItemAction}>{row.locationName}</LinkAnchor>;
        },
      },
      {
        key: "createdAt",
        name: t("inventory.incomingAt"),
        sortable: true,
        resizable: true,
        formatter: ({ row }) => {
          return <Flex align="center">{timeUtil(row?.createdAt as string)}</Flex>;
        },
      },
      {
        key: "closedQuantity",
        name: t("inventory.closeQuantity"),
        sortable: true,
        resizable: true,
        formatter: ({ row }) => (
          <Flex justify="flex-end">
            {row.closedQuantity !== null
              ? setToLocaleString(row?.closedQuantity) + " " + row.unitText
              : ""}
          </Flex>
        ),
      },
      {
        key: "closedAt",
        name: t("inventory.closeAt"),
        sortable: true,
        resizable: true,
        width: 160,
        formatter: ({ row }) => {
          return (
            <Flex align="center">
              {row?.closedAt === null ? "" : dayjs(row.closedAt).format("YYYY-MM-DD HH:mm:ss")}
            </Flex>
          );
        },
      },
      {
        key: "increaseQuantity",
        name: t("inventory.addQuantity"),
        sortable: true,
        resizable: true,
        formatter: ({ row }) => {
          const betweenIncreaseQuantity = row?.betweenIncreaseQuantity ?? 0;
          return (
            <Flex justify="flex-end">
              {setToLocaleString(betweenIncreaseQuantity)} {row.unitText}
            </Flex>
          );
        },
      },
      {
        key: "decreaseQuantity",
        name: t("inventory.subtractQuantity"),
        sortable: true,
        resizable: true,
        formatter: ({ row }) => {
          const betweenDecreaseQuantity = row?.betweenDecreaseQuantity ?? 0;
          return (
            <Flex justify="flex-end">
              {setToLocaleString(betweenDecreaseQuantity)} {row.unitText}
            </Flex>
          );
        },
      },
      {
        key: "deltaQuantity",
        name: t("inventory.changeQuantity"),
        sortable: true,
        resizable: true,
        formatter: ({ row }) => {
          const betweenDeltaQuantity = row?.betweenDeltaQuantity ?? 0;
          return (
            <Flex justify="flex-end">
              {setToLocaleString(betweenDeltaQuantity)} {row.unitText}
            </Flex>
          );
        },
      },
      {
        key: "quantityAtStart",
        name: t("inventory.carryOverQuantity"),
        sortable: true,
        resizable: true,
        formatter: ({ row }) => {
          const quantityAtStart = row?.quantityAtStart ?? 0;
          return (
            <Flex justify="flex-end">
              {setToLocaleString(quantityAtStart)} {row.unitText}
            </Flex>
          );
        },
      },
      {
        key: "quantity",
        name: t("inventory.totalQuantity"),
        sortable: true,
        resizable: true,

        formatter: ({ row }) => {
          const quantityAtEnd = row?.quantityAtEnd ?? 0;
          const detailItemAction = () => {
            openModal(
              <LedgerDetailForm data={row} />,
              null,
              `${t("common.detail", { item: `${t("inventory.inventory")}` })}`,
            );
          };
          return (
            <Flex h="100%" justify="flex-end" align="center">
              <LinkAnchor onClick={detailItemAction}>
                {setToLocaleString(quantityAtEnd)} {row.unitText}
              </LinkAnchor>
            </Flex>
          );
        },
      },
    ];
  }, [openModal, t]);

  useEffect(() => {
    const updatedParams = {
      targetDateString: dayjs(startDate).startOf("day").toISOString(),
      targetDateEndString: dayjs(endDate).endOf("day").toISOString(),
    };

    setParams((prev) => ({
      ...prev,
      ...updatedParams,
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  useEffect(() => {
    // 경남케미칼 커스텀
    if (customFunctions.ADD_INVENTORY_QUANTITY_SORT_FIX && !sort.length) {
      setSort(() => ["-quantity"]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  // 서버에서 locations 정보를 가져온 후 한번만 초기화시킴
  useEffect(() => {
    if (!chipInitRef.current && locations && locations.length > 0 && chipState.length === 0) {
      console.log("Call useEffect locationChipInfo (초기 1회만)");

      // 1. locationChipInfo 상태 저장
      const chipInfo: LocationChipInfo =
        locations.reduce((acc, item) => {
          acc[item.value] = item.label;
          return acc;
        }, {} as LocationChipInfo) ?? {};

      setLocationChipInfo(chipInfo);

      // 2. 기존 쿼리에서 필터 정보 추출
      const orQuery = query.find((q) => q && "$or" in q);
      const orQueryData = orQuery?.$or;

      const response: string[] = [];

      if (orQueryData && Array.isArray(orQueryData?.[0]?.locationCode)) {
        response.push(...orQueryData[0].locationCode);
      }

      // 3. chipState 동시 설정 (locationChipInfo 참조 X)
      setChipState(response.length > 0 ? response : Object.keys(chipInfo));

      // 4. 초기화 플래그 설정
      chipInitRef.current = true;
    }
  }, [locations, chipState.length]);

  // 검색값 초기화(resetQueryStrings() 호출) 시 query에 따라 chipState 초기설정
  useEffect(() => {
    console.log("Call useEffect query");
    if (!customFunctions.ADD_INVENTORY_LOCATION_FILTER) return;
    if (!locationChipInfo) return; // 아직 초기화 안됐으면 리턴

    const prevQuery = prevQueryRef.current;
    const isPrevNotEmpty = Array.isArray(prevQuery) && prevQuery.length > 0;
    const isNowEmpty = Array.isArray(query) && query.length === 0;

    // 이전 query는 있었고, 지금 query는 빈 배열이면 resetQueryStrings가 호출된 것으로 간주
    if (isPrevNotEmpty && isNowEmpty) {
      console.log("resetQueryStrings 호출 감지됨");

      // chipState 초기화 또는 비즈니스 로직 실행
      if (locationChipInfo) {
        setChipState(Object.keys(locationChipInfo));
      }

      setSelectedRows(new Set());
    }

    // 항상 현재 값을 다음 렌더에서 사용할 수 있도록 저장
    prevQueryRef.current = query;
  }, [query, locationChipInfo]);

  // chipState 변경 시 query에 반영
  useEffect(() => {
    console.log("Call useEffect chipState");

    if (!customFunctions.ADD_INVENTORY_LOCATION_FILTER) return;
    setQuery((prev) => {
      const safeChipState = chipState ?? [];

      const newOr = [{ locationCode: safeChipState }];
      const prevOr = prev.find((q) => "$or" in q)?.$or ?? [];

      // query가 이미 동일하면 변경하지 않음
      if (JSON.stringify(prevOr) === JSON.stringify(newOr)) return prev;

      const filtered = prev.filter((q) => !("$or" in q));

      return [...filtered, { $or: newOr }];
    });
  }, [chipState]);

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <ActionHeader />
      <DateHeader />
      {
        <Flex px="sm" direction="column" gap="xs">
          <Text fz="xl" fw={600}>
            {"외주입고등록"}
          </Text>
          <Text fz="xs">{"우리 회사에서 다루는 외주입고에 대해 조회합니다."}</Text>
        </Flex>
      }
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={t("common.filterDescription")}>
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      {customFunctions.ADD_INVENTORY_LOCATION_FILTER && locationChipInfo && (
        <Flex justify="space-between" align="flex-start" px="sm">
          <Flex wrap="wrap" styles={flexGrowable}>
            <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
              {Object.keys(locationChipInfo).map((key: string) => {
                return (
                  <Checkbox
                    value={key}
                    label={locationChipInfo[key]}
                    checked={chipState && chipState.includes(key)}
                    onChange={(e) => {
                      const targetValue = e.currentTarget.value;
                      if (chipState.includes(targetValue)) {
                        setChipState(chipState.filter((chip: string) => chip !== targetValue));
                      } else {
                        setChipState([...chipState, targetValue]);
                      }
                    }}
                  />
                );
              })}
            </Flex>
          </Flex>
        </Flex>
      )}
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<InventoriesBetweenGet200Response, InventoriesBetweenGet200ResponseRowsInner>
            columns={columns}
            rowKeyGetter={(row: InventoriesBetweenGet200ResponseRowsInner) => JSON.stringify(row)}
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<InventoriesBetweenGet200Response> />
      </Flex>
    </Flex>
  );
};
